export const redirectQueryParam = 'r'
export const webAppAuthQueryParam = 'app'
export const redeemAuthQueryParam = 'redeem'
export const galleryAuthQueryParam = 'gallery'
export const subscribeAuthQueryParam = 'subscribe'
export const checkoutAuthQueryParam = 'checkout'
export const redemptionAuthQueryParam = 'redeem'
export const orgAuthQueryParam = 'org'
export const ambassadorCheckoutAuthQueryParam = 'ambassadorCheckout'
export const eventCheckoutAuthQueryParam = 'eventCheckout'
export const schoolCheckoutAuthQueryParam = 'schoolCheckout'
export const referralAuthQueryParam = 'referral'
export const desktopAuthQueryParam = 'desktop'
export const mcpAuthQueryParam = 'mcp'
// Return leg of the desktop browser handoff and of an MCP sign-in: the API's authorize URL,
// strictly validated by the login service before redirecting. Named distinctly from
// the login service's own `redirect` routing param.
export const desktopReturnToQueryParam = 'returnTo'
/**
 * The desktop handoff's return leg may only target the API's own desktop-oauth
 * authorize endpoint (which re-validates client, loopback redirect_uri, and PKCE).
 * Anything else — other origins, other paths, a fragment or userinfo, unparseable values — is
 * treated as absent, so a crafted link can't turn the redirect into an open one. A fragment or
 * userinfo is rejected rather than stripped: downstream pages interpolate the accepted value
 * into a redirect.
 */
export function validatedDesktopOAuthReturnUrl(
  value: string | string[] | undefined,
  apiServiceUrl: string
): string | undefined {
  return validatedApiReturnUrl(value, apiServiceUrl, '/desktop-oauth/authorize')
}

/**
 * The MCP authorize endpoint's logged-out bounce, held to the same rules as the desktop return
 * leg; that endpoint re-validates the client, its redirect_uri and PKCE before acting.
 */
export function validatedMcpOAuthReturnUrl(
  value: string | string[] | undefined,
  apiServiceUrl: string
): string | undefined {
  return validatedApiReturnUrl(value, apiServiceUrl, '/mcp/oauth/authorize')
}

/** The `returnTo` validator for an `r=desktop` or `r=mcp` sign-in, each held to its own authorize endpoint. */
export function authorizeReturnUrlValidator(redirectParam: unknown): typeof validatedDesktopOAuthReturnUrl | undefined {
  if (redirectParam === desktopAuthQueryParam) return validatedDesktopOAuthReturnUrl
  if (redirectParam === mcpAuthQueryParam) return validatedMcpOAuthReturnUrl
  return undefined
}

function validatedApiReturnUrl(
  value: string | string[] | undefined,
  apiServiceUrl: string,
  pathname: string
): string | undefined {
  if (typeof value !== 'string') {
    return undefined
  }
  let url: URL
  let apiOrigin: string
  try {
    url = new URL(value)
    // An unparseable base (a misconfigured environment) must reject the value, not throw
    // out of a redirect handler.
    apiOrigin = new URL(apiServiceUrl).origin
  } catch {
    return undefined
  }
  if (url.origin !== apiOrigin || url.pathname !== pathname) {
    return undefined
  }
  if (url.hash !== '' || url.username !== '' || url.password !== '') {
    return undefined
  }
  return url.toString()
}

/**
 * Set by the login page a shell or MCP sign-in opens, read by the API's magic-link hop: the emailed link's
 * `returnTo` is honored only in this browser, the one with a client to return to. The value is
 * already public in the URL (see `authorizeAttemptState`), so the cookie proves which browser, not a secret.
 */
export const desktopSignInAttemptCookie = 'nb-desktop-signin'
/** The shell's loopback listener lifetime; set once per attempt, so the cookie can't outlive the port it vouches for. */
export const desktopSignInAttemptCookieMaxAgeSeconds = 15 * 60

/**
 * What keys the sign-in attempt cookie for an already-validated authorize URL: its `state`, or for a client
 * that omits `state` (OAuth 2.1 allows it with PKCE), its required `code_challenge`.
 */
export function authorizeAttemptState(validatedReturnTo: string | undefined): string | undefined {
  if (validatedReturnTo === undefined) return undefined
  const params = new URL(validatedReturnTo).searchParams
  return params.get('state') || params.get('code_challenge') || undefined
}

export const appSubscriptionCompleteQueryParam = 'subscriptionComplete'
export const subscriptionReturnToAppRoute = '/return-to-app'
export const businessReturnToAppRoute = '/business-return-to-app'
export const loginErrorQueryParam = 'errorKey'
export const logoutTitleQueryParam = 'title'
export const logoutMessageQueryParam = 'message'
export const redemptionCodeQueryParam = 'redemptionCode'
export const referralCodeQueryParam = 'referralCode'
export const srcQueryParam = 'src'
/** The shared note a sign-in is for, carried from the note link's preview page through to the web app. */
export const noteIdQueryParam = 'note-id'
export const connectCalendarQueryParam = 'connectCalendar'
/** Which provider's connect button the desktop handoff landing should render (a GraphQL `OAuthProvider` value, e.g. `MICROSOFT`). */
export const connectCalendarProviderQueryParam = 'provider'
/** Per-attempt id and starting button, so the browser leg's events join the shell leg's. */
export const connectCalendarHandoffIdQueryParam = 'handoffId'
export const connectCalendarSourceQueryParam = 'connectSource'
// The shell reads its scope flags at the click and passes them on, since the browser leg may not
// have experiment values yet when the user clicks.
/** `1` when the shell wants the browser leg to add the calendar-list scope to the consent. */
export const connectCalendarListScopeQueryParam = 'includeCalendarList'
/** `1` when the shell wants the browser leg to ask for the single `calendar.readonly` scope. */
export const connectCalendarReadonlyScopeQueryParam = 'readonlyCalendarScope'
/**
 * The shell's loopback URL the browser leg reports its outcome to, by navigating there once the
 * consent settles. Absent from a URL an older shell built.
 */
export const connectCalendarReturnUrlQueryParam = 'returnUrl'
/**
 * Set by the shell's loopback page when it forwards the browser back to the landing page: how the
 * consent it just reported ended. The shell can't import this package, so keep the two in sync.
 */
export const connectCalendarHandoffOutcomeQueryParam = 'handoffOutcome'
/**
 * The browser leg's attempt count for the handoff, sent on its report and echoed by the shell's
 * forward back, so a retry after a reported failure keeps counting where the reload cut it off.
 */
export const connectCalendarHandoffAttemptQueryParam = 'handoffAttempt'

// The shell account the handoff belongs to. The browser leg runs in whatever session the OS
// browser holds, which may be another account entirely; without this it would connect a calendar
// to that account and the shell would find nothing. An opaque id, never the email: this URL
// reaches Mixpanel and Sentry as `$current_url`.
export const connectCalendarUserIdQueryParam = 'connectCalendarUserId'

/**
 * Where the desktop sign-in browser leg lands once the loopback listener has the code: the shell's
 * own success page can't reach the store or the API, so it redirects here to render the real one.
 * The shell builds that URL and can't import this package, so keep the two in sync.
 */
export const desktopSignInSuccessQueryParam = 'desktopSignInSuccess'

// The shell registers this scheme itself (main.ts, electron-builder.config.ts) and can't import
// this package, so keep the two in sync.
export const desktopCalendarConnectedDeepLink = 'notability-desktop://calendar-connected'
